import type { TravelSearchSelection } from '../../api/travel-search-config'

import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { clsx } from 'clsx'
import { format } from 'date-fns'

import { LinkButton } from '@dfds-pax/shared-components/client'

import { isSogRoute } from '../Utils/sog-helpers'
import { LoadDataFromParams } from '../Utils/url-helpers'

import { useLoadTravelDatesIntoContext } from '../Hooks/dates/useLoadTravelDatesIntoContext'
import { useSyncDatesFallback } from '../Hooks/dates/useSyncDatesFallback'
import { useTravelDates } from '../Hooks/dates/useTravelDates'
import { useLoadRoutesIntoContext } from '../Hooks/routes/useLoadRoutesIntoContext'
import { useRoutes } from '../Hooks/routes/useRoutes'
import { getSession, useBookingUrl } from '../Hooks/useBookingUrl'
import { useKeepScrollPosition } from '../Hooks/useKeepScrollPosition'
import { useSyncTravelSearchSettings } from '../Hooks/useSyncTravelSearchSettings'
import { UseTravelSearchConfigResult } from '../Hooks/useTravelSearchConfig'
import { useWindowWidth2 } from '../Hooks/useWindowWidth2'
import { useLoadVehiclesIntoContext } from '../Hooks/vehicles/useLoadVehiclesIntoContext'
import { useVehicles } from '../Hooks/vehicles/useVehicles'

import { ActiveSelectField, useActiveSelectField } from '../context/ActiveSelectFieldProvider'
import { useTravelSearchContext } from '../context/TravelSearchProvider'
import { getAppMinibookId, getAppTravelSearchId, toShortDateString } from '../Utils'
import { logFrontendInfo } from '../Utils/logger'
import { getMinibookIdFromSession } from '../Utils/minibookid-session'
import { TrackingCommand, TravelSearchTrackingTypes } from '../Utils/tracking'
import { getTravelSearchIdFromSession } from '../Utils/travel-search-session'
import CurrencySelector from './currency/CurrencySelector'
import DateSelector from './date/DateSelector'
import { BackToMultiOffer } from './multioffer/BackToMultiOffer'
import MultiOffer from './multioffer/MultiOffer'
import OnewayReturn from './oneway-return/OnewayReturn'
import PassengersSelector from './passengers/PassengersSelector'
import RoutesSelector from './routes/RoutesSelector'
import StickyButton from './StickyButton'
import VehiclesSelector from './vehicles/VehiclesSelector'

interface Props {
  errorMessage?: string
  travelSearchConfigResult: UseTravelSearchConfigResult
}

export const TravelSearch: React.FC<Props> = ({ errorMessage, travelSearchConfigResult }) => {
  const stickyTravelSearchRef = useRef<HTMLInputElement>(null)
  // eslint-disable-next-line react-hooks/refs
  const height: number = stickyTravelSearchRef.current?.getBoundingClientRect().height ?? 0
  const {
    dispatch,
    state: { disableOneWay, isOneWay, selectedCurrency },
  } = useTravelSearchContext()
  const { activeField, travelSearchInViewport } = useActiveSelectField()

  const { queryData } = LoadDataFromParams()
  const travelSearchRef = useRef<HTMLDivElement>(null as unknown as HTMLDivElement)
  const [showMultiOffer, setShowMultiOffer] = useState<boolean | undefined>()
  const [isButtonLoading, setIsButtonLoading] = useState(false)
  const [selectedMinibookId, setSelectedMinibookId] = useState<string>()
  const configuredMinibookId =
    !travelSearchConfigResult.isMultiOffer && typeof travelSearchConfigResult.availableMinibooks === 'string'
      ? travelSearchConfigResult.availableMinibooks
      : undefined
  const minibookId =
    selectedMinibookId ??
    queryData.minibookId ??
    getAppMinibookId(window.location.host) ??
    configuredMinibookId ??
    getMinibookIdFromSession()
  const travelSearchId =
    queryData.travelSearchId ??
    getAppTravelSearchId(window.location.host) ??
    travelSearchConfigResult.travelSearchId ??
    getTravelSearchIdFromSession()
  const source = travelSearchConfigResult.source

  let selection: TravelSearchSelection | undefined
  if (!travelSearchConfigResult.isLoading) {
    if (source === 'legacy' && minibookId) {
      selection = { minibookId, source }
    } else if (source === 'shared' && travelSearchId) {
      selection = { source, travelSearchId }
    }
  }

  const { t } = useTranslation()

  useLoadRoutesIntoContext(selection)
  useLoadVehiclesIntoContext(queryData.offerCode ?? '')
  useLoadTravelDatesIntoContext(selectedCurrency, queryData.offerCode ?? '')
  useSyncDatesFallback()

  //This hook handles site effects when interconnected data changes
  useSyncTravelSearchSettings()

  const { isRoutesLoading, selectedRoute } = useRoutes()
  const { isVehiclesLoading, selectedPassengers, selectedVehicle, vehicleApiData } = useVehicles()
  const { isTravelDatesLoading, selectedHomeboundDate, selectedOutboundDate } = useTravelDates()
  const outboundDate = selectedOutboundDate ? format(selectedOutboundDate, 'yyyy-MM-dd') : undefined

  const { isMobile, isTablet } = useWindowWidth2()

  // const travelSearchConfigResult = useTravelSearchConfig()
  useKeepScrollPosition(isMobile || isTablet, activeField !== ActiveSelectField.NONE)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShowMultiOffer(travelSearchConfigResult?.isMultiOffer)
  }, [travelSearchConfigResult.isMultiOffer, travelSearchConfigResult.availableMinibooks])

  useEffect(() => {
    dispatch({
      dates: {
        homeboundDate: queryData.homeboundDate ? new Date(queryData.homeboundDate) : undefined,
        outboundDate: queryData.outboundDate ? new Date(queryData.outboundDate) : undefined,
      },
      type: 'SET_DATES',
    })
  }, [dispatch, queryData.outboundDate, queryData.homeboundDate, queryData.oneWay])

  const bookingUrlResult = useBookingUrl(selection?.source === 'legacy' ? selection.minibookId : '', selectedCurrency, queryData.offerCode)

  //tracking
  useEffect(() => {
    if (
      !travelSearchConfigResult.isLoading &&
      !isTravelDatesLoading &&
      !isRoutesLoading &&
      !isVehiclesLoading &&
      selectedOutboundDate &&
      !isOneWay &&
      selectedHomeboundDate
    ) {
      TrackingCommand(
        TravelSearchTrackingTypes.INITIALIZE,
        selectedRoute,
        {
          homeboundDate: selectedHomeboundDate,
          outboundDate: selectedOutboundDate,
        },
        selectedVehicle,
        vehicleApiData,
        selectedPassengers,
        selectedCurrency,
        isOneWay
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    travelSearchConfigResult.isLoading,
    isTravelDatesLoading,
    isVehiclesLoading,
    isTravelDatesLoading,
    selectedHomeboundDate,
    selectedOutboundDate,
    isOneWay,
  ])

  const isLoading: boolean =
    travelSearchConfigResult?.isLoading ||
    isTravelDatesLoading ||
    isRoutesLoading ||
    (selectedRoute?.routeCode && !isSogRoute(selectedRoute?.routeCode, outboundDate) && isVehiclesLoading) ||
    toShortDateString(selectedOutboundDate) === '-'

  if (errorMessage) {
    return (
      <div className={clsx('relative')}>
        <div className="flex h-full w-full text-semantic-global-text-light">
          <span className="mt-auto">{window.location.hostname !== 'www.dfds.com' ? errorMessage : null}</span>
        </div>
      </div>
    )
  }

  return (
    <div className={clsx('relative scroll-mt-4')} id="travel-search-container" ref={travelSearchRef}>
      <StickyButton
        offersShown={showMultiOffer === true}
        onClick={() => {
          if (showMultiOffer === true) {
            return
          }
          travelSearchRef?.current?.scrollIntoView({ behavior: 'smooth' })
        }}
        shown={travelSearchInViewport ? activeField === ActiveSelectField.NONE : false}
      />

      <div
        className={clsx(
          'transition-[top] duration-300 ease-in-out',
          travelSearchInViewport &&
            'z-10 bg-semantic-global-background-sand lg:fixed lg:left-0 lg:right-0 lg:top-0 lg:shadow-[0_2px_8px_rgba(0,0,0,0.1)]'
        )}
        ref={stickyTravelSearchRef}
        style={{ top: travelSearchInViewport ? 0 : -height }}
      >
        <div
          className={clsx(
            travelSearchInViewport &&
              'w-auto py-semantic-global-md mx-(--grid-margin,8px) max-w-(--grid-width,100%) px-(--grid-gutter,16px)'
          )}
        >
          {!travelSearchConfigResult.isLoading && showMultiOffer && typeof travelSearchConfigResult.availableMinibooks != 'string' ? (
            <MultiOffer
              onProductClicked={(_minibookId: string) => {
                setSelectedMinibookId(_minibookId)
                setShowMultiOffer(false)
              }}
              optionalOfferTitle={travelSearchConfigResult.optionalOfferTitle}
              productLists={travelSearchConfigResult.availableMinibooks}
              route={travelSearchConfigResult.route || ''}
            />
          ) : (
            <>
              {!travelSearchConfigResult.isLoading && travelSearchConfigResult.isMultiOffer ? (
                <div className="ml-auto mb-semantic-global-md flex md:hidden">
                  <BackToMultiOffer
                    isMultiOffer={travelSearchConfigResult.isMultiOffer}
                    onMultiOfferClicked={() => {
                      setShowMultiOffer(true)
                    }}
                  />
                </div>
              ) : null}

              {!travelSearchConfigResult.isLoading ? (
                <div className="flex flex-col gap-semantic-global-xs">
                  <div className="flex flex-row">
                    <OnewayReturn onewayDisabled={disableOneWay} />

                    {travelSearchConfigResult.isMultiOffer ? (
                      <div className="ml-auto hidden md:block">
                        <BackToMultiOffer
                          isMultiOffer={travelSearchConfigResult.isMultiOffer}
                          onMultiOfferClicked={() => {
                            setShowMultiOffer(true)
                          }}
                        />
                      </div>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-12 gap-semantic-global-xs bg-transparent lg:flex lg:gap-0">
                    <RoutesSelector travelSearchRef={travelSearchRef} />
                    <VehiclesSelector />
                    <PassengersSelector />
                    <CurrencySelector />
                    <DateSelector />
                    {isSogRoute(selectedRoute?.routeCode ?? '', outboundDate) ? <div className="col-span-1" /> : null}
                    <div className="col-span-12 lg:flex-1">
                      <LinkButton
                        className={clsx('relative h-13.5 w-full rounded-none focus-visible:z-10 focus-visible:outline-offset-2! md:h-14.5')}
                        data-e2e="booking-link"
                        fullWidth
                        href={bookingUrlResult.url}
                        isLoading={bookingUrlResult.isLoading || isLoading || isButtonLoading}
                        onClick={() => {
                          setIsButtonLoading(true)

                          TrackingCommand(
                            TravelSearchTrackingTypes.SUBMIT,
                            selectedRoute,
                            {
                              homeboundDate: selectedHomeboundDate,
                              outboundDate: selectedOutboundDate,
                            },
                            selectedVehicle,
                            vehicleApiData,
                            selectedPassengers,
                            selectedCurrency,
                            isOneWay
                          )
                          if (bookingUrlResult?.url?.includes('yourbooking')) {
                            const sessionId = getSession()?.sessionId ?? ''
                            logFrontendInfo('PAX-OB-BOOKING-URL', {
                              bookingUrl: bookingUrlResult.url,
                              path: window.location?.pathname,
                              sessionId,
                            })
                          }
                          setIsButtonLoading(false)
                        }}
                        onKeyDown={(event) => {
                          // Anchors only activate on Enter by default; add Space to match button semantics.
                          if (event.key !== ' ') return
                          event.preventDefault()
                          event.currentTarget.click()
                        }}
                        rel="nofollow"
                        variant="primary"
                      >
                        <span className="max-w-50 truncate">{t('SEE-PRICES')}</span>
                      </LinkButton>
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
